import React, { useState, useEffect, useContext } from "react";
import Container from "components/shared/Container";
import useLocalStorage from "hooks/useLocalStorage";
import useFetch from "hooks/useFetch";
import appConfig from "config.json";
import { getCdn, isCurrentPathPresent } from "utils";
import { getCookie } from "utils/cookie";
import { trackLink } from "utils/analyticsFramework";
import { Context } from "Context";
import Image from "components/shared/Image";
import Message from "components/message-center/Message";
import MessagesContentContainer from "./MessagesContentContainer";
import Icon from "components/shared/Icon";

const { content, brands } = appConfig;
const iconsPath = `${getCdn()}/logos/message-center/`;
const mainClass = "syndicated-message-center";
const mainClassHeader = `${mainClass}__header`;
const messageIdPrefix = "async-global-message-";
const processMessageContent = (content, store, brand) => {
	if (content && content !== "&nbsp;" && content?.trim() !== "") {
		if (store && brands[brand]?.keepInFlowStores?.includes(store)) {
			return content.replaceAll('target="_self"', 'target="_blank"');
		}
		return content;
	} else {
		return false;
	}
};

const removeDuplicates = (messageIds) => {
	return messageIds.filter((id, index, result) => {
		return result.indexOf(id) === index;
	});
};

const MessageCenter = () => {
	const {
		config: { brand, theme, store, locale },
		updatedLocation,
	} = useContext(Context);

	const [storageViewedMessages, setStorageViewedMessages] = useLocalStorage(
		"messageCenterViewed",
		[]
	);
	const [localStorageMessageCenterData, setLocalStorageMessageCenterData] =
		useLocalStorage("MessageCenterData", []);
	const [toggle, setToggle] = useState(true);
	const [showViewed, setShowViewed] = useState(false);
	const [messagesData, setMessagesData] = useState(null);
	const [configuration, setConfiguration] = useState(null);
	const [messagesViewed, setMessagesViewed] = useState([]);
	const [messages, setMessages] = useState([]);
	const [overrideCollapsedMessages, setOverrideCollapsedMessages] = useState(
		[]
	);

	const { response, error } = useFetch(content.apiUrl.messageCenter);

	const hasToDisplay = (message) => {
		return (
			!getCookie(message.id) &&
			!isCurrentPathPresent(message.exclude || [], updatedLocation) &&
			isCurrentPathPresent(message.pages, updatedLocation)
		);
	};

	const processMessages = (messages) => {
		return (
			messages
				?.filter((message) => {
					const id =
						`${messageIdPrefix}${message.type}-${message.content?.length}`.replace(
							//eslint-disable-next-line no-useless-escape
							/[^A-Za-z0-9\-]/g,
							""
						);
					message.id = id;
					return hasToDisplay(message);
				})
				.map((message) => {
					if (message.type === "terms")
						message.expiration = appConfig.cookieExpirationInDays;
					message.viewed = storageViewedMessages?.indexOf(message.id) !== -1;
					message.type = message.type?.toLowerCase().replace(/\s/g, "_");
					message.content = processMessageContent(
						message.content,
						store,
						brand
					);
					return message;
				}) || []
		);
	};

	const removeIdsFromViewed = (messageIds) => {
		return messageIds.length
			? storageViewedMessages.filter(
					(viewed) => messageIds.indexOf(viewed) === -1
			  )
			: [];
	};

	const toggleMessagesCenterHandler = (toggleNewValue) => {
		setToggle(!toggleNewValue);
		const messagesIds = messages.map(({ id }) => id);
		const messagesViewedIds = messagesViewed.map(({ id }) => id);
		if (toggle || (!toggle && !toggleNewValue)) {
			setStorageViewedMessages(
				removeDuplicates(
					storageViewedMessages.concat(messagesIds).concat(messagesViewedIds)
				)
			);
		} else {
			setStorageViewedMessages(removeIdsFromViewed(messagesViewedIds));
		}
		setOverrideCollapsedMessages([]);
		setToggle((prevState) => !prevState);
		if (!toggleNewValue) {
			processLocalStorageMessages();
		}
	};

	const toggleMessagesViewedHandler = () => {
		const messagesViewedIds = messagesViewed.map(({ id }) => id);
		setStorageViewedMessages(removeIdsFromViewed(messagesViewedIds));
		setShowViewed((prevState) => !prevState);
	};

	const processLocalStorageMessages = () => {
		try {
			let newMessageCenterData = { messages: {} };
			messagesData?.forEach((message) => {
				newMessageCenterData.messages[message.id] = {
					id: message.id,
					content: message.content,
				};
			});
			newMessageCenterData["created_on"] = new Date().toISOString();
			setLocalStorageMessageCenterData({ ...newMessageCenterData });
		} catch (error) {
			console.log(error);
		}
	};

	const processMessagesData = (
		newMessagesData,
		setCollapsedState = false,
		setToggleValue = true
	) => {
		try {
			const viewed = [];
			const notViewed = [];
			const newOverrideCollapsedMessages = [];
			const expiration =
				Number(response.descriptions?.[0]?.sections?.expiration) || 1;
			newMessagesData.forEach((message) => {
				if (message.viewed) {
					viewed.push(message);
				} else {
					notViewed.push(message);
					if (message.overrideCollapsed) {
						newOverrideCollapsedMessages.push(message);
					}
				}
			});
			setOverrideCollapsedMessages(newOverrideCollapsedMessages);
			setMessagesViewed(viewed);
			setMessages(notViewed);
			if (setToggleValue) {
				if (newMessagesData.length === newOverrideCollapsedMessages.length) {
					setToggle(true);
				} else {
					setToggle(setCollapsedState ? false : !!notViewed.length);
				}
			}
			setShowViewed(!(!notViewed.length && viewed.length));
			setMessagesData(newMessagesData);
			if (localStorageMessageCenterData?.messages) {
				let resetLocalStorage = false;
				const createdOn = new Date(localStorageMessageCenterData?.created_on);
				const hourDiff = Math.abs(
					(createdOn.getTime() - new Date().getTime()) / 1000 / (60 * 60)
				);
				if (!expiration || expiration <= hourDiff) {
					resetLocalStorage = true;
				} else {
					for (let index = 0; index < newMessagesData.length; index++) {
						if (
							!(
								newMessagesData[index]["id"] in
								localStorageMessageCenterData?.messages
							) ||
							(newMessagesData[index]["id"] in
								localStorageMessageCenterData?.messages &&
								newMessagesData[index].content !==
									localStorageMessageCenterData?.messages[
										newMessagesData[index]["id"]
									]?.content)
						) {
							resetLocalStorage = true;
							break;
						}
					}
				}
				if (resetLocalStorage) {
					setLocalStorageMessageCenterData({});
					setStorageViewedMessages([]);
					setToggle(true);
				}
			}
		} catch (error) {
			setLocalStorageMessageCenterData({});
			setStorageViewedMessages([]);
			setToggle(true);
			console.log(error);
		}
	};

	const closeMessageHandler = (id) => {
		if (id && brand) {
			const updatedModel = {
				linkId: `${brand.toUpperCase()}_${id}_Close`,
			};
			trackLink(updatedModel, "ViewModal");
		}
		processMessagesData(
			messagesData.filter((message) => message.id !== id),
			false,
			false
		);
	};

	const renderMessage = (message) => (
		<Message
			key={message.id}
			closeMessageHandler={closeMessageHandler}
			closeText={configuration.closeText}
			data={message}
		/>
	);

	useEffect(() => {
		if (response) {
			response.descriptions?.length &&
				setConfiguration(response.descriptions?.[0]?.sections);
			response.alerts &&
				processMessagesData(
					processMessages(response.alerts),
					response.descriptions?.[0]?.sections?.collapsed === "true"
				);
		}
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [response, store, updatedLocation]);

	if (error || !response) {
		return null;
	}

	const hideToggleContainer = (
		<Container>
			<button
				className={`${mainClass}__toggle ${mainClass}__toggle--${brand}${
					theme ? ` syndicated-theme--${theme}` : ""
				}`}
				tabIndex="0"
				role="region"
				onClick={() => {
					toggleMessagesCenterHandler(false);
				}}
			>
				<span>{configuration?.viewLessText}</span>
				<Icon icon="norgie-up" loading="eager" />
			</button>
		</Container>
	);

	return (
		configuration &&
		!!messagesData?.length && (
			<div
				className={`${mainClass} ${mainClass}--${locale} ${mainClass}--${brand} ${
					theme ? ` syndicated-theme--${theme}` : ""
				}`}
				data-nosnippet
				style={{ visibility: "hidden" }}
			>
				<div className={mainClassHeader}>
					<Container direction="row">
						<div
							className={`${mainClassHeader}__icon ${mainClass}__icon-container`}
							aria-hidden="true"
						>
							<Image
								image={{
									url: `${iconsPath}header.svg`,
									title: "message-center-information",
									loading: "eager",
								}}
							/>
						</div>
						<div className={`${mainClassHeader}__title`}>
							{configuration.title}
						</div>
						{!toggle && (
							<button
								className={`${mainClassHeader}__toggle ${mainClassHeader}__toggle--${brand}${
									theme ? ` syndicated-theme--${theme}` : ""
								}`}
								tabIndex="0"
								onClick={() => {
									toggleMessagesCenterHandler(true);
								}}
							>
								<span>{configuration.readMoreText}</span>
								<Icon icon="norgie-opened" loading="eager" />
							</button>
						)}
					</Container>
				</div>
				{toggle && (
					<MessagesContentContainer>
						{showViewed && !!messagesViewed.length && (
							<div className={`${mainClass}__viewed-toggle-container`}>
								<button
									className={`${mainClass}__viewed-toggle`}
									onClick={toggleMessagesViewedHandler}
									tabIndex={0}
								>
									{(messagesViewed.length > 1
										? configuration.viewedMessagesPlural || ""
										: configuration.viewedMessagesSingular || ""
									).replace("%s", messagesViewed.length)}
								</button>
							</div>
						)}
						{!showViewed && messagesViewed.map(renderMessage)}
						{messages.map(renderMessage)}
						{hideToggleContainer}
					</MessagesContentContainer>
				)}
				{!toggle &&
					configuration.collapsed === "true" &&
					!!overrideCollapsedMessages.length && (
						<MessagesContentContainer>
							{overrideCollapsedMessages.map(renderMessage)}
							{hideToggleContainer}
						</MessagesContentContainer>
					)}
			</div>
		)
	);
};

export default MessageCenter;
